import type { AwaitingStatusModalProps, CoApplicant } from '../../types/modals'
import { useEffect, useMemo, useState } from 'react'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import { updateApplicationStatus } from '../../services/applicationService'
import { showSuccess, showError } from '@/lib/utils/notifications'
import modalStyles from '../../styles/modals.module.css'



const tid = 'application-modal-awaiting-status'

/** Awaiting flow for multi-applicant apps — Angular set-applicant-status + submitAwaitingForm. */
export function AwaitingStatusModal({
  applicantId,
  applicationAllData,
  onClose,
  onSuccess,
}: AwaitingStatusModalProps) {
  const [lockApplicants, setLockApplicants] = useState<Array<string | number>>([])
  const [loading, setLoading] = useState(false)

  const coResidents = useMemo<CoApplicant[]>(() => {
    const raw = applicationAllData?.co_resident_application
    if (!raw) return []
    if (Array.isArray(raw)) return raw as CoApplicant[]
    return Object.values(raw) as CoApplicant[]
  }, [applicationAllData])

  useEffect(() => {
    const initial: Array<string | number> = []
    coResidents.forEach((co) => {
      if (co?.applicant_lock === '1' && co?.applicant_id != null) {
        initial.push(co.applicant_id)
      }
    })
    setLockApplicants(initial)
  }, [coResidents])

  const toggleApplicant = (applicant_id: string | number) => {
    setLockApplicants((prev) =>
      prev.includes(applicant_id)
        ? prev.filter((id) => id !== applicant_id)
        : [...prev, applicant_id],
    )
  }

  const handleSubmit = async () => {
    setLoading(true)
    try {
      await updateApplicationStatus(applicantId, {
        applicant_status: 'AWAITING',
        credit_report_process: '',
        lock_applicants: lockApplicants,
      })
      showSuccess('Status Changed Successfully!')
      onSuccess()
      onClose()
    } catch (error: unknown) {
      const msg = (error as { message?: unknown })?.message
      showError(typeof msg === 'string' && msg ? msg : 'Failed to update status to AWAITING')
    } finally {
      setLoading(false)
    }
  }

  return (
    <OnScreenModal
      isOpen={true}
      title="Status Update"
      onClose={onClose}
      maxWidth="max-w-xl"
      headerVariant="centeredTitle"
      overlayDataTestId={tid}
      panelDataTestId={`${tid}-panel`}
      titleDataTestId={`${tid}-title`}
      closeButtonDataTestId={`${tid}-close-btn`}
      footer={
        <div className={modalStyles.modalFooterActionGroup} data-testid={`${tid}-footer`}>
          <button
            type="button"
            className={modalStyles.modalActionPrimary}
            onClick={handleSubmit}
            disabled={loading}
            data-testid={`${tid}-confirm-btn`}
          >
            {loading ? 'Processing...' : 'Awaiting'}
          </button>
          <button
            type="button"
            className={modalStyles.modalActionSecondary}
            onClick={onClose}
            data-testid={`${tid}-cancel-btn`}
          >
            Cancel
          </button>
        </div>
      }
    >
      <div className="flex flex-col gap-4 text-left text-slate-800" data-testid={`${tid}-body`}>
        <p className="text-base font-semibold text-slate-800" data-testid={`${tid}-heading`}>Update status to Awaiting</p>
        <p className="text-sm leading-relaxed text-slate-600" data-testid={`${tid}-description`}>
          Please select below to lock any co-applicant.
        </p>

        <div className="flex flex-col gap-3 rounded-md border border-slate-200 bg-slate-50/80 px-4 py-3" data-testid={`${tid}-co-applicants-list`}>
          {coResidents.length === 0 ? (
            <p className="text-sm text-slate-500 italic" data-testid={`${tid}-empty`}>No co-applicants.</p>
          ) : (
            coResidents.map((co) => {
              const id = co.applicant_id
              const isChecked = lockApplicants.includes(id)
              const fullName =
                `${co.user_data?.first_name ?? ''} ${co.user_data?.last_name ?? ''}`.trim() ||
                `Applicant ${id}`
              return (
                <label
                  key={String(id)}
                  className="flex cursor-pointer items-center gap-3 text-sm text-slate-800"
                  data-testid={`${tid}-row-${id}-label`}
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4 shrink-0 cursor-pointer rounded border-slate-300"
                    checked={isChecked}
                    onChange={() => toggleApplicant(id)}
                    data-testid={`${tid}-row-${id}-input`}
                  />
                  <span>
                    {fullName}
                    {co.applicant_status ? (
                      <span className="text-slate-500"> [{co.applicant_status}]</span>
                    ) : null}
                  </span>
                </label>
              )
            })
          )}
        </div>
      </div>
    </OnScreenModal>
  )
}
